import React, { useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/data';
import SubmissionReview from './SubmissionReview';
import TopFiveReview from './TopFiveReview';
import LeaderboardView from './LeaderboardView';
import HappySnapModeration from './HappySnapModeration';
import CompetitionSlideshow from './CompetitionSlideshow';
import AnnouncementGenerator from './AnnouncementGenerator';
import ChampionPointsPanel from './ChampionPointsPanel';
import NeedsReviewPanel from './NeedsReviewPanel';
import CompetitionStatePanel from './CompetitionStatePanel';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { deriveLifecycle } from '@shared/competitionRules';

const client = generateClient({ authMode: 'userPool' });

type TabType = 'submissions' | 'top5' | 'leaderboard' | 'happy-snaps' | 'champion-points';

interface AdjudicationDashboardProps {
  eventId: string;
}

const AdjudicationDashboard: React.FC<AdjudicationDashboardProps> = ({ eventId }) => {
  const [event, setEvent] = useState<any>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = React.useRef(false);
  const [activeTab, setActiveTab] = useState<TabType>('submissions');
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [championData, setChampionData] = useState<{ seniorStandings: any[]; juniorStandings: any[] }>({ seniorStandings: [], juniorStandings: [] });
  const [championNoIdentity, setChampionNoIdentity] = useState<any[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const lb = await (client.queries as any).calculateLeaderboard({ eventId });
      const data = parseLambdaResponse(lb?.data);
      if (!data?.success) throw new Error('Failed to load leaderboard');
      const categories = Object.entries(data.categoryRankings || {}).map(([species, entries]: [string, any]) => ({
        species,
        entries: (entries as any[]).map((e: any, i: number) => ({ rank: i + 1, competitorName: e.competitorName, lengthCm: e.lengthCm, keptOrReleased: e.keptOrReleased || '', isMysteryFish: e.isMysteryFish })),
      }));
      const { generateLeaderboardPdf } = await import('../../utils/generateLeaderboardPdf');
      generateLeaderboardPdf({
        eventTitle: event?.title || 'Competition',
        eventDate: event?.eventDate || '',
        categories,
        mysteryFishSpecies: event?.mysteryFishSpecies || undefined,
        mysteryFishSize: event?.mysteryFishSize || undefined,
        totalSubmissions: data.totalFishCount || submissions.length,
        approvedSubmissions: submissions.filter((s: any) => s.status === 'APPROVED').length,
      });
    } catch (err: any) {
      showToast(err.message || 'Failed to generate PDF', 'error');
    }
    setExportingPdf(false);
  };

  const loadData = useCallback(async () => {
    try {
      // Show the full-page loader only on the first load. A refresh (after an
      // approve, rotate or transition) keeps the screen mounted, so an open
      // catch dialog or the Needs review panel isn't torn down.
      if (!hasLoadedRef.current) setLoading(true);
      const eventResult = await client.models.Event.get({ id: eventId });
      if (eventResult.data) setEvent(eventResult.data);

      /*
        Paginate. `limit: 1000` is NOT a guarantee of completeness: with a
        filter, AppSync runs a Scan with a FilterExpression and the limit caps
        rows SCANNED, not rows returned — the filter is applied afterwards, so a
        page can come back partially filled (or empty) while more matches exist
        behind a nextToken. Same fault fixed in CompetitionDayDashboard on
        competition morning, 20 Sep 2026, where check-ins appeared to lag.

        Getting this wrong here is worse than on the check-in list: a catch
        missing from adjudication is a competitor silently left out of the
        results.
      */
      let subs: any[] = [];
      let subsToken: string | null = null;
      do {
        const page: any = await (client.models as any).CatchSubmission.list({
          filter: { eventId: { eq: eventId } },
          limit: 1000,
          ...(subsToken ? { nextToken: subsToken } : {}),
        });
        subs = subs.concat((page.data || []).filter(Boolean));
        subsToken = page.nextToken ?? null;
      } while (subsToken);

      // Merge AI verification advisory (admin-only model) onto each submission
      // so existing UI that reads sub.aiSuspicionScore / aiFlags etc keeps working.
      try {
        let verRows: any[] = [];
        let verToken: string | null = null;
        do {
          const verPage: any = await (client.models as any).CatchVerification.list({
            limit: 2000,
            ...(verToken ? { nextToken: verToken } : {}),
          });
          verRows = verRows.concat(verPage.data || []);
          verToken = verPage.nextToken ?? null;
        } while (verToken);
        const verByCatch: Record<string, any> = {};
        // Skip null items (AppSync returns null for a row with a resolver
        // error), so one bad row can't drop the AI data for every catch.
        for (const v of verRows.filter(Boolean)) {
          if (v.catchId) verByCatch[v.catchId] = v;
        }
        for (const s of subs) {
          const v = verByCatch[s.id];
          if (v) {
            s.aiVerificationStatus = v.aiVerificationStatus;
            s.aiSuspicionScore = v.aiSuspicionScore;
            s.aiFlags = v.aiFlags;
            s.aiVerifiedAt = v.aiVerifiedAt;
          }
        }
      } catch (vErr) {
        console.warn('Could not load AI verification data:', vErr);
      }

      setSubmissions(subs);

      // Load champion points from leaderboard
      try {
        const lb = await (client.queries as any).calculateLeaderboard({ eventId });
        const lbData = parseLambdaResponse(lb?.data);
        if (lbData?.championPoints) setChampionData(lbData.championPoints);
        // A8: placed catches with no resolvable member identity, admin only.
        setChampionNoIdentity(Array.isArray(lbData?.championNoIdentity) ? lbData.championNoIdentity : []);
      } catch { /* champion points are non-critical */ }
    } catch (err) {
      console.error('Error loading adjudication data:', err);
      showToast('Failed to load data', 'error');
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { loadData(); }, [loadData]);

  const lifecycle = deriveLifecycle(event || {}, new Date());

  // Stats
  const approved = submissions.filter((s: any) => s.status === 'APPROVED').length;
  const disqualified = submissions.filter((s: any) => s.status === 'DISQUALIFIED').length;
  const pending = submissions.filter((s: any) => s.status === 'PENDING' || !s.status).length;
  const totalFish = submissions.length;

  // AI verification stats
  const aiCompleted = submissions.filter((s: any) => s.aiVerificationStatus === 'COMPLETED');
  const aiHigh = aiCompleted.filter((s: any) => (s.aiSuspicionScore || 0) >= 60).length;
  const aiMedium = aiCompleted.filter((s: any) => (s.aiSuspicionScore || 0) >= 30 && (s.aiSuspicionScore || 0) < 60).length;
  const aiLow = aiCompleted.filter((s: any) => (s.aiSuspicionScore || 0) < 30).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-600">Loading adjudication data...</div>
      </div>
    );
  }

  const tabs: { key: TabType; label: string }[] = [
    { key: 'submissions', label: 'Submissions' },
    { key: 'top5', label: 'Top 6 Review' },
    { key: 'leaderboard', label: 'Leaderboard' },
    { key: 'happy-snaps', label: 'Happy Snaps' },
    ...(lifecycle.adjudicated ? [{ key: 'champion-points' as TabType, label: 'Champion Points' }] : []),
  ];

  return (
    <div>
      {toast && (
        <div role="alert" aria-live="assertive" className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{event?.title || 'Competition'}</h2>
          </div>
          {lifecycle.published && (
            <div className="flex gap-2">
              <button onClick={() => setSlideshowOpen(true)} className="px-4 py-2 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 rounded-md">🎬 Start Slideshow</button>
              <button onClick={() => setAnnouncementOpen(true)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300">📝 Generate Announcement</button>
              <button onClick={handleExportPdf} disabled={exportingPdf} className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:bg-gray-300">{exportingPdf ? 'Generating...' : '📄 Download PDF'}</button>
            </div>
          )}
        </div>
      </div>

      {/* The derived phase chip, status lines, quick actions, decisions and
          publish/unpublish — shared with CompetitionSetup (#134 D1/D2). */}
      <div className="mb-6">
        {event && (
          <CompetitionStatePanel
            event={event}
            submissions={submissions}
            championStandings={championData}
            onRefresh={loadData}
          />
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <div className="bg-white shadow rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{totalFish}</div>
          <div className="text-xs text-gray-500">Total Submissions</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{approved}</div>
          <div className="text-xs text-gray-500">Approved</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{disqualified}</div>
          <div className="text-xs text-gray-500">Disqualified</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{pending}</div>
          <div className="text-xs text-gray-500">Pending Review</div>
        </div>
        <div className="bg-white shadow rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-fishing-blue">{approved + pending}</div>
          <div className="text-xs text-gray-500">Fish Count</div>
        </div>
      </div>

      {/* AI Verification Summary */}
      {aiCompleted.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white shadow rounded-lg p-3 text-center border-l-4 border-l-red-400">
            <div className="text-xl font-bold text-red-600">{aiHigh}</div>
            <div className="text-xs text-gray-500">High Suspicion (60–100)</div>
          </div>
          <div className="bg-white shadow rounded-lg p-3 text-center border-l-4 border-l-amber-400">
            <div className="text-xl font-bold text-amber-600">{aiMedium}</div>
            <div className="text-xs text-gray-500">Medium (30–59)</div>
          </div>
          <div className="bg-white shadow rounded-lg p-3 text-center border-l-4 border-l-green-400">
            <div className="text-xl font-bold text-green-600">{aiLow}</div>
            <div className="text-xs text-gray-500">Low/Clear (0–29)</div>
          </div>
        </div>
      )}

      {/* Needs review (#144 R2): placed or flagged catches not yet checked by
          a person. Shown above the tabs so it is the first thing an admin
          sees; clears as catches are approved/disqualified via loadData. */}
      <NeedsReviewPanel submissions={submissions} onRefresh={loadData} mystery={event} published={lifecycle.published} />

      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-fishing-blue text-fishing-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'submissions' && (
        <SubmissionReview submissions={submissions} eventId={eventId} onRefresh={loadData} mystery={event} published={lifecycle.published} />
      )}
      {activeTab === 'top5' && (
        <TopFiveReview submissions={submissions} eventId={eventId} onRefresh={loadData} mystery={event} published={lifecycle.published} />
      )}
      {activeTab === 'leaderboard' && (
        <LeaderboardView eventId={eventId} />
      )}
      {activeTab === 'happy-snaps' && (
        <HappySnapModeration eventId={eventId} onRefresh={loadData} />
      )}
      {activeTab === 'champion-points' && (
        <ChampionPointsPanel
          eventId={eventId}
          adjudicated={lifecycle.adjudicated}
          published={lifecycle.published}
          seniorStandings={championData.seniorStandings}
          juniorStandings={championData.juniorStandings}
          confirmedSeniorChampion={event?.seniorChampionName ? { name: event.seniorChampionName, points: event.seniorChampionPoints || 0, memberSub: event.seniorChampionMemberSub || '' } : null}
          confirmedJuniorChampion={event?.juniorChampionName ? { name: event.juniorChampionName, points: event.juniorChampionPoints || 0, memberSub: event.juniorChampionMemberSub || '' } : null}
          onChampionConfirmed={loadData}
          championNoIdentity={championNoIdentity}
        />
      )}

      {slideshowOpen && <CompetitionSlideshow eventId={eventId} onExit={() => setSlideshowOpen(false)} />}
      {announcementOpen && <AnnouncementGenerator eventId={eventId} eventTitle={event?.title || ''} eventDate={event?.eventDate || ''} onClose={() => setAnnouncementOpen(false)} />}
    </div>
  );
};

export default AdjudicationDashboard;
